import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../../contexts/LanguageContext'
import LanguageFlagIcon from './LanguageFlagIcon'

export default function LanguageSwitcher() {
  const { t } = useTranslation()
  const { currentLanguage, languages, setLanguage } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef(null)

  const activeLanguage = languages.find((l) => l.code === currentLanguage) || languages[0]

  useEffect(() => {
    if (!isOpen) return undefined
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    const handleKey = (event) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [isOpen])

  const handleSelect = (code) => {
    setLanguage(code)
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-label={t('layout.languageSwitcher.buttonLabel')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={t('layout.languageSwitcher.buttonLabel')}
        className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-100 transition-colors overflow-hidden p-0.5"
      >
        {activeLanguage?.flagCountry && (
          <LanguageFlagIcon
            flagCountry={activeLanguage.flagCountry}
            className="w-full h-full rounded-full"
          />
        )}
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label={t('layout.languageSwitcher.listboxLabel')}
          className="absolute right-0 mt-2 w-56 bg-white rounded-theme-md shadow-lg border border-gray-200 py-1 z-50"
        >
          {languages.map((lang) => {
            const isActive = lang.code === currentLanguage
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                  isActive
                    ? 'bg-theme-primary/10 text-theme-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {lang.flagCountry && (
                  <LanguageFlagIcon
                    flagCountry={lang.flagCountry}
                    className="w-5 h-5 rounded-full flex-shrink-0"
                  />
                )}
                <span className="truncate">{lang.nativeLabel}</span>
                {isActive && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-theme-primary flex-shrink-0" />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
